import React, { useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { caseStudies } from "../utils/caseStudyData";
import {
  Container,
  Typography,
  Box,
  Grid,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const CaseStudyDetailPage = () => {
  const { id } = useParams();
  const study = caseStudies.find((s) => s.id === id);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!study) {
    return (
      <Container sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h4">Case Study Not Found</Typography>
        <Button
          component={RouterLink}
          to="/"
          variant="contained"
          sx={{ mt: 4 }}
        >
          Back to Home
        </Button>
      </Container>
    );
  }

  // Helper function to check if data exists and is not empty
  const hasData = (data) => {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === "string") return data.trim().length > 0;
    return true;
  };

  // Image gallery handlers
  const openImageModal = (imageIndex) => {
    setCurrentImageIndex(imageIndex);
    setSelectedImage(study.gallery[imageIndex]);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % study.gallery.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(study.gallery[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex =
      (currentImageIndex - 1 + study.gallery.length) % study.gallery.length;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(study.gallery[prevIndex]);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#ffffff" }}>
      {/* Navigation Header */}
      <Box sx={{ borderBottom: "1px solid #f0f0f0", py: 2 }}>
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#666",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": { backgroundColor: "transparent", color: "#000" },
            }}
          >
            Back to Case Studies
          </Button>
        </Container>
      </Box>

      {/* Hero Section - Minimal and Clean */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: "#000",
                  mt: hasData(study.clientName) ? 1 : 0,
                  mb: 3,
                }}
              >
                {study.title}
              </Typography>
              {hasData(study.shortDescription) && (
                <Typography
                  variant="h6"
                  sx={{
                    color: "#666",
                    fontWeight: 400,
                    lineHeight: 1.6,
                    fontSize: "1.1rem",
                  }}
                >
                  {study.shortDescription}
                </Typography>
              )}
            </Box>
          </Grid>
          {hasData(study.imageUrl) && (
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={study.imageUrl}
                alt={study.title}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Image Gallery Section - Horizontal Carousel */}
      {hasData(study.gallery) && (
        <Box sx={{ backgroundColor: "#f8f9fa", py: 6 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h3"
              sx={{
                fontSize: "2rem",
                fontWeight: 700,
                color: "#000",
                mb: 4,
                textAlign: "center",
              }}
            >
              Project Gallery
            </Typography>
            <Box sx={{ position: "relative", px: 8 }}>
              {/* Left Arrow - Fixed positioning */}
              <IconButton
                sx={{
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(0,0,0,0.8)",
                  color: "white",
                  width: 48,
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.95)",
                    transform: "translateY(-50%) scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
                onClick={() => {
                  const container =
                    document.getElementById("gallery-container");
                  container.scrollBy({ left: -400, behavior: "smooth" });
                }}
              >
                <ArrowBackIosIcon sx={{ marginLeft: "4px" }} />
              </IconButton>
              {/* Right Arrow - Fixed positioning */}
              <IconButton
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(0,0,0,0.8)",
                  color: "white",
                  width: 48,
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.95)",
                    transform: "translateY(-50%) scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
                onClick={() => {
                  const container =
                    document.getElementById("gallery-container");
                  container.scrollBy({ left: 400, behavior: "smooth" });
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
              {/* Horizontal Scrolling Container */}
              <Box
                id="gallery-container"
                sx={{
                  display: "flex",
                  gap: 3,
                  overflowX: "auto",
                  scrollBehavior: "smooth",
                  py: 2,
                  px: 1,
                  "&::-webkit-scrollbar": {
                    height: 6,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: 3,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: 3,
                    "&:hover": {
                      backgroundColor: "#a8a8a8",
                    },
                  },
                }}
              >
                {study.gallery.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => openImageModal(index)}
                    sx={{
                      width: "400px",
                      height: "280px",
                      minWidth: "400px",
                      borderRadius: "16px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                      overflow: "hidden",
                      position: "relative",
                      "&:hover": {
                        transform: "translateY(-6px) scale(1.02)",
                        boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={typeof image === "string" ? image : image.url}
                      alt={
                        typeof image === "string"
                          ? `Gallery image ${index + 1}`
                          : image.alt || `Gallery image ${index + 1}`
                      }
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top center",
                        borderRadius: "16px",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ backgroundColor: "#ffffff", py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            {/* Content Column - Only show if there's content */}
            <Grid
              item
              xs={12}
              md={
                hasData(study.results) || hasData(study.technologies) ? 8 : 12
              }
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {/* Project Overview */}
                {hasData(study.projectOverview) && (
                  <ContentSection
                    title="Project Overview"
                    content={study.projectOverview}
                  />
                )}
                {/* The Challenge */}
                {hasData(study.challenges) && (
                  <ContentSection
                    title="The Challenge"
                    content={
                      Array.isArray(study.challenges)
                        ? study.challenges.join(" ")
                        : study.challenges
                    }
                  />
                )}
                {/* Our Solution */}
                {hasData(study.solution) && (
                  <ContentSection
                    title="Our Solution"
                    content={study.solution}
                  />
                )}
              </Box>
            </Grid>
            {/* Sidebar - Only show if there's sidebar content */}
            {(hasData(study.results) || hasData(study.technologies)) && (
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "sticky",
                    top: "2rem",
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    p: 4,
                    border: "1px solid #f0f0f0",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* Results */}
                  {hasData(study.results) && (
                    <>
                      <SidebarSection title="Key Results">
                        <Typography
                          variant="body1"
                          sx={{
                            color: "#333",
                            lineHeight: 1.7,
                            fontSize: "0.95rem",
                          }}
                        >
                          {study.results}
                        </Typography>
                      </SidebarSection>
                      {hasData(study.technologies) && (
                        <Divider sx={{ my: 4 }} />
                      )}
                    </>
                  )}
                  {/* Technologies */}
                  {hasData(study.technologies) && (
                    <SidebarSection title="Technologies">
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {study.technologies.map((tech) => (
                          <Chip
                            key={tech}
                            label={tech}
                            sx={{
                              backgroundColor: "#f5f5f5",
                              color: "#333",
                              fontWeight: 500,
                              fontSize: "0.8rem",
                              height: "28px",
                              "&:hover": {
                                backgroundColor: "#ebebeb",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </SidebarSection>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Client Testimonial Section */}
      {hasData(study.testimonial) && (
        <Box sx={{ backgroundColor: "#f8f9fa", py: 8 }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                textAlign: "center",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#000",
                  mb: 4,
                }}
              >
                What Our Client Says
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "24px",
                  p: 6,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  position: "relative",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: 400,
                    color: "#333",
                    lineHeight: 1.6,
                    fontStyle: "italic",
                    mb: 4,
                    mt: 2,
                  }}
                >
                  "{study.testimonial.quote}"
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                  }}
                >
                  {study.testimonial.avatar && (
                    <Box
                      component="img"
                      src={study.testimonial.avatar}
                      alt={study.testimonial.name}
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "3px solid #f0f0f0",
                      }}
                    />
                  )}
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#000",
                        fontSize: "1.1rem",
                      }}
                    >
                      {study.testimonial.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: "0.9rem",
                      }}
                    >
                      {study.testimonial.title}
                      {study.testimonial.company &&
                        `, ${study.testimonial.company}`}
                    </Typography>
                  </Box>
                </Box>
                {/* Star Rating */}
                {study.testimonial.rating && (
                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    {[...Array(5)].map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          color:
                            index < study.testimonial.rating
                              ? "#ffd700"
                              : "#e0e0e0",
                          fontSize: "1.2rem",
                        }}
                      >
                        ★
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Bottom CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            backgroundColor: "#000",
            borderRadius: "20px",
            color: "#fff",
          }}
        >
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            Ready to start your project?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
            Let's discuss how we can help bring your vision to life.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            href="https://calendar.app.google/YW4z4CA9cnMwW7WC7"
          >
            Get in Touch
          </Button>
        </Box>
      </Container>

      {/* Enhanced Full-Screen Image Modal */}
      <Dialog
        open={!!selectedImage}
        onClose={closeImageModal}
        maxWidth={false}
        fullScreen // Make modal truly full-screen
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "#000",
            margin: 0, // Remove default MUI margins
            maxHeight: "100vh",
            maxWidth: "100vw",
            borderRadius: "0", // Remove border radius
            overflow: "hidden", // Prevent accidental overflow
          },
        }}
      >
        <DialogContent
          sx={{
            p: 2,
            position: "relative",
            minHeight: "100vh",
            backgroundColor: "#000",
            overflowX: "hidden", // Hide horizontal scrollbar
            overflowY: "auto", // Allow vertical scrolling if needed
            "&::-webkit-scrollbar": {
              width: 12,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.3)",
              borderRadius: 6,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.5)",
              },
            },
          }}
        >
          {/* Top Bar with Counter and Close */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 100%)",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "white",
                fontWeight: 500,
                backgroundColor: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                px: 2,
                py: 1,
                borderRadius: "20px",
                fontSize: "0.9rem",
              }}
            >
              {currentImageIndex + 1} of {study.gallery?.length || 0}
            </Typography>
            <IconButton
              onClick={closeImageModal}
              sx={{
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "white",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Arrows */}
          {study.gallery && study.gallery.length > 1 && (
            <>
              <IconButton
                onClick={prevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "white",
                  backdropFilter: "blur(10px)",
                  width: 56,
                  height: 56,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ArrowBackIosIcon fontSize="large" />
              </IconButton>
              <IconButton
                onClick={nextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "white",
                  backdropFilter: "blur(10px)",
                  width: 56,
                  height: 56,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ArrowForwardIosIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {/* Main Image - Now dynamically scaled */}
          {selectedImage && (
            <Box
              component="img"
              src={
                typeof selectedImage === "string"
                  ? selectedImage
                  : selectedImage.url
              }
              alt={
                typeof selectedImage === "string"
                  ? "Gallery image"
                  : selectedImage.alt || "Gallery image"
              }
              sx={{
                maxWidth: "100vw", // Constrained to viewport width
                maxHeight: "100vh", // Constrained to viewport height
                width: "auto",
                height: "auto",
                borderRadius: "8px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                display: "block",
                margin: "0 auto", // Center horizontally
                position: "relative",
                top: "50%",
                transform: "translateY(-50%)", // Center vertically
              }}
            />
          )}

          {/* Keyboard Navigation Hint */}
          <Box
            sx={{
              position: "absolute",
              bottom: 80,
              right: 20,
              zIndex: 3,
              color: "rgba(255,255,255,0.6)",
              fontSize: "0.8rem",
              textAlign: "right",
            }}
          >
            <Typography variant="caption" display="block">
              ← → Navigate
            </Typography>
            <Typography variant="caption" display="block">
              ESC Close
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Content Section Component
const ContentSection = ({ title, content }) => (
  <Box>
    <Typography
      variant="h3"
      sx={{
        fontSize: "2rem",
        fontWeight: 700,
        color: "#000",
        mb: 3,
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontSize: "1.1rem",
        lineHeight: 1.8,
        color: "#444",
        letterSpacing: "0.3px",
      }}
    >
      {content}
    </Typography>
  </Box>
);

// Sidebar Section Component
const SidebarSection = ({ title, children }) => (
  <Box>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#000",
        mb: 2,
        fontSize: "1rem",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

export default CaseStudyDetailPage;
